import { ApolloError } from '@apollo/client';
import { Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { AIOnboardingSuggestionDrawer } from '@/modules/aiSuggestions/components/AIOnboardingSuggestionDrawer';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { ClientOrganizationModal } from '@/modules/clientOrganizations/ClientOrganizationModal';
import { ClientProfileSideDrawer } from '@/modules/clientProfiles/ClientProfileForm/ClientProfileModal';
import { useOnHouseholdWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnHouseholdWorkflowTrackerComplete';
import { ClientPeopleAutoOnboardedBanner } from '@/pages/ClientPages/ClientPeoplePage/components/ClientPeopleAutoOnboardedBanner/ClientPeopleAutoOnboardedBanner';
import { IntegrationClientKind, WorkflowTrackerKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  mapDataToTableRows,
  PeopleListSuggestion,
} from './ClientPeopleListView.utils';
import { ClientDetailsPrimaryClients } from './ClientPeopleListViewGrantors';
import { ClientDetailsPeopleOrganizationsTable } from './ClientPeopleListViewTable';
import { useClientPeopleListViewTableQuery } from './graphql/ClientPeopleListViewTable.generated';

export interface ClientPeopleListViewProps {
  householdId: string;
}

function ClientPeopleListViewInner({ householdId }: ClientPeopleListViewProps) {
  const canEdit = useCanEditHouseholdData();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [isPrimaryClient, setPrimaryClient] = useState<boolean>(false);
  const { data, loading, refetch } = useClientPeopleListViewTableQuery({
    variables: { householdId },
    fetchPolicy: 'cache-and-network',
    onError: (error: ApolloError) => {
      showFeedback(
        'Could not fetch client details. Please refresh the page to try again.'
      );
      reportError('Caught error when loading household info', error);
    },
  });
  const refreshPeople = useCallback(() => {
    void refetch().catch((error: unknown) => {
      reportError(
        'Failed to refresh automatically added people and organizations',
        error instanceof Error ? error : new Error(String(error))
      );
    });
  }, [refetch, reportError]);

  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.DocumentExtraction,
    onCompleted: refreshPeople,
  });
  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.AiSuggestionsV2HouseholdRegen,
    onCompleted: refreshPeople,
  });

  const primaryClients = getNodes(data?.primaryClients);
  const household = getNodes(data?.households)[0];
  const integrationClientIds = useMemo(() => {
    return (
      household?.integrationClients
        ?.filter((client) => client.kind !== IntegrationClientKind.Salesforce)
        .map((client) => client.id) ?? []
    );
  }, [household?.integrationClients]);
  const listContent = mapDataToTableRows(data);
  const [
    clientProfileModalState,
    {
      openModal: openClientProfileModal,
      setModalIsOpen: setClientProfileModalIsOpen,
    },
  ] = useModalState<string | null>();

  const [existingPrimaryClientId, setExistingPrimaryClientId] = useState<
    string | null
  >(null);
  const [
    clientOrganizationModalState,
    {
      openModal: openClientOrganizationModal,
      setModalIsOpen: setClientOrganizationModalOpen,
    },
  ] = useModalState<string | null>();

  // Modal state for Suggestion Document Drawer
  const [suggestionDrawerState, suggestionDrawerModal] = useModalState<{
    suggestionId: string;
  }>();

  const handleLocateSuggestion = useCallback(
    (suggestion: PeopleListSuggestion) => {
      suggestionDrawerModal.openModal({ suggestionId: suggestion.id });
    },
    [suggestionDrawerModal]
  );

  return (
    <>
      <Stack
        direction="column"
        width="100%"
        spacing={2}
        p={2}
        // Fill the height established by ClientPeoplePage's `height: 100%` stack
        // so the empty state (fill="flex") can center within the space that
        // remains below the primary-client and suggestion cards.
        flex={1}
        minHeight={0}
      >
        {canEdit && (
          <ClientPeopleAutoOnboardedBanner
            householdId={householdId}
            newIndividualsAutomaticallyOnboardedAt={
              household?.newIndividualsAutomaticallyOnboardedAt
            }
          />
        )}
        <ClientDetailsPrimaryClients
          primaryClients={primaryClients}
          loading={loading}
          householdId={householdId}
          integrationClientIds={integrationClientIds}
          onClick={(clientProfileId: string | null) => {
            setPrimaryClient(true);
            if (
              clientProfileId === null &&
              primaryClients.length === 1 &&
              primaryClients[0]?.id
            ) {
              setExistingPrimaryClientId(primaryClients[0].id);
            }
            openClientProfileModal(clientProfileId);
          }}
        />
        <ClientDetailsPeopleOrganizationsTable
          listContent={listContent}
          loading={loading}
          openClientProfileModal={(clientProfileId: string | null) => {
            const isEditingPrimary = primaryClients.some(
              (c) => c.id === clientProfileId
            );
            setPrimaryClient(isEditingPrimary);
            openClientProfileModal(clientProfileId);
          }}
          openClientOrganizationModal={openClientOrganizationModal}
          onLocateSuggestionClick={handleLocateSuggestion}
        />
      </Stack>
      <ClientProfileSideDrawer
        isOpen={clientProfileModalState.isModalOpen}
        setIsOpen={setClientProfileModalIsOpen}
        householdId={householdId}
        clientProfileId={clientProfileModalState.data}
        clientProfileTypeDisplay={isPrimaryClient ? 'client' : 'individual'}
        isPrimary={isPrimaryClient}
        forceGrantorIfNoClientProfileId={isPrimaryClient} // default grantor to true when adding a new primary client
        allowDelete={isPrimaryClient ? primaryClients.length > 1 : true}
        existingPrimaryClientId={existingPrimaryClientId}
      />
      <ClientOrganizationModal
        isOpen={clientOrganizationModalState.isModalOpen}
        setIsOpen={setClientOrganizationModalOpen}
        organizationId={clientOrganizationModalState.data}
        householdId={householdId}
      />
      {suggestionDrawerState.isModalOpen && suggestionDrawerState.data && (
        <AIOnboardingSuggestionDrawer
          isOpen={suggestionDrawerState.isModalOpen}
          onClose={() => suggestionDrawerModal.closeModal()}
          suggestionId={suggestionDrawerState.data.suggestionId}
        />
      )}
    </>
  );
}

export function ClientPeopleListView() {
  const householdId = useRequiredParam('householdId');
  return <ClientPeopleListViewInner householdId={householdId} />;
}
